import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const http = axios.create({
    baseURL: API,
    withCredentials: true,
});

// ---------- Global "network busy" tracker ----------
// Every axios request increments a counter; every response (success or error) decrements it.
// While the counter > 0, we:
//   - set body[data-busy="1"] → CSS turns the cursor into a "progress" cursor globally
//   - dispatch a `busychange` event → consumed by <GlobalBusyIndicator /> to animate a top bar
let _pendingCount = 0;
function _emitBusy() {
    if (typeof document === "undefined") return;
    const busy = _pendingCount > 0;
    document.body.dataset.busy = busy ? "1" : "0";
    window.dispatchEvent(new CustomEvent("busychange", { detail: { pending: _pendingCount, busy } }));
}
http.interceptors.request.use((config) => {
    _pendingCount += 1;
    _emitBusy();
    return config;
});
http.interceptors.response.use(
    (resp) => { _pendingCount = Math.max(0, _pendingCount - 1); _emitBusy(); return resp; },
    (err) => {
        _pendingCount = Math.max(0, _pendingCount - 1); _emitBusy();
        // Global 401 handler: a stale/expired JWT would otherwise surface a
        // cryptic "Not authenticated (HTTP 401)" toast on every admin action.
        // Instead, tag the error with a friendlier French message AND emit an
        // event the AuthProvider consumes to clear local user state — the app
        // then re-renders with the login gate visible.
        try {
            const status = err?.response?.status;
            const url = err?.config?.url || "";
            // Ignore 401s from the identity-check endpoint (`/auth/me` is
            // expected to 401 for anonymous visitors on every page load).
            if (status === 401 && /\/auth\/(login|register)/.test(url)) {
                if (err?.response) {
                    err.response.data = { ...(err.response.data || {}), detail: "E-mail ou mot de passe incorrect." };
                }
            } else if (status === 401 && !url.includes("/auth/me")) {
                if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("auth:expired"));
                }
                if (err?.response) {
                    err.response.data = {
                        ...(err.response.data || {}),
                        detail: "Session expirée — reconnecte-toi pour continuer.",
                    };
                }
            }
        } catch { /* never let the interceptor itself crash */ }
        return Promise.reject(err);
    },
);

function fromDetail(detail) {
    if (detail == null) return null;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .join(" ");
    if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
    try { return JSON.stringify(detail); } catch { return String(detail); }
}

export function formatApiError(input) {
    if (input == null) return "Une erreur est survenue.";
    // Axios error (has .isAxiosError / .response / .message)
    if (typeof input === "object" && (input.isAxiosError || input.response || input.request || input.message)) {
        const status = input.response?.status;
        const data = input.response?.data;
        const fromData = fromDetail(data?.detail) || (typeof data === "string" ? data : null);
        if (fromData) return status ? `${fromData} (HTTP ${status})` : fromData;
        if (input.message) return status ? `${input.message} (HTTP ${status})` : input.message;
        return status ? `Erreur HTTP ${status}` : "Une erreur réseau est survenue.";
    }
    // Direct detail payload
    return fromDetail(input) || "Une erreur est survenue.";
}
